import * as React from 'react';

import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button, buttonVariants } from './button';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
	<nav
		role="navigation"
		aria-label="pagination"
		className={cn('mx-auto flex w-full justify-center', className)}
		{...props}
	/>
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
	HTMLUListElement,
	React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
	<ul
		ref={ref}
		className={cn('flex flex-row items-center gap-1', className)}
		{...props}
	/>
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
	<li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
	isActive?: boolean
} & React.ComponentProps<typeof Button>;

const PaginationLink = ({
	className,
	isActive,
	size = 'icon',
	...props
}: PaginationLinkProps) => (
	<Button
		aria-current={isActive ? 'page' : undefined}
		className={cn(
			buttonVariants({
				variant: isActive ? 'default' : 'ghost',
				size
			}),
			className,
			isActive ? 'hover:bg-primary' : '',
			'font-normal'
		)}
		variant={isActive ? 'default' : 'ghost'}
		type="button"
		{...props}
	/>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Go to previous page"
		size="default"
		className={cn('gap-1 pl-2.5', className)}
		{...props}
	>
		<ChevronLeft className="h-4 w-4" />
	</PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Go to next page"
		size="default"
		className={cn('gap-1 pr-2.5', className)}
		{...props}
	>
		<ChevronRight className="h-4 w-4" />
	</PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
	className,
	...props
}: React.ComponentProps<'span'>) => (
	<span
		aria-hidden
		className={cn('flex h-9 w-9 items-center justify-center', className)}
		{...props}
	>
		<MoreHorizontal className="h-4 w-4" />
		<span className="sr-only">More pages</span>
	</span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

const PaginationItems = ({
	total = 1,
	active,
	size,
	onClick
}: {
	total?: number,
	active: number,
	size: number,
	onClick: (page: number) => void
}) => {
	const getPaginationPage = (total: number, size: number): number => {
		return Math.ceil(total / size);
	};

	const pagination = getPaginationPage(total, size);
	const render = [];

	render.push(
		<PaginationItem>
			<PaginationPrevious
				key="prev"
				disabled={pagination === 1 || active === 1}
				onClick={() => onClick(active - 2)}
				className="rounded px-3 py-2 disabled:opacity-50"
			/>
		</PaginationItem>
	);

	if (pagination <= 5) {
		for (let i = 1; i <= pagination; i++) {
			render.push(
				<PaginationItem>
					<PaginationLink
						key={i}
						onClick={() => onClick(i - 1)}
						className={cn(`rounded px-3 py-2`, {
							'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
								active === i,
							'text-secondary bg-white': active !== i
						})}
					>
						{i}
					</PaginationLink>
				</PaginationItem>
			);
		}
	} else {
		render.push(
			<PaginationItem>
				<PaginationLink
					key={'first'}
					onClick={() => onClick(0)}
					className={cn(`rounded px-3 py-2`, {
						'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
							active === 1,
						'text-secondary bg-white': active !== 1
					})}
				>
					1
				</PaginationLink>
			</PaginationItem>
		);

		if (active > 3) {
			render.push(
				<PaginationItem>
					<PaginationEllipsis />
				</PaginationItem>
			);
		}

		const start = Math.max(2, active - 1);
		const end = Math.min(pagination - 1, active + 1);

		for (let i = start; i <= end; i++) {
			render.push(
				<PaginationItem>
					<PaginationLink
						key={i}
						onClick={() => onClick(i - 1)}
						className={cn(`rounded px-3 py-2`, {
							'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
								active === i,
							'text-secondary bg-white': active !== i
						})}
					>
						{i}
					</PaginationLink>
				</PaginationItem>
			);
		}

		if (active < pagination - 2) {
			render.push(
				<PaginationItem>
					<PaginationEllipsis />
				</PaginationItem>
			);
		}

		render.push(
			<PaginationItem>
				<PaginationLink
					key={pagination}
					onClick={() => onClick(pagination - 1)}
					className={cn(`rounded px-3 py-2`, {
						'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
							active === pagination,
						'text-secondary bg-white': active !== pagination
					})}
				>
					{pagination}
				</PaginationLink>
			</PaginationItem>
		);
	}

	if (pagination === 0) {
		render.push(
			<PaginationItem>
				<PaginationLink
					key={1}
					className={`bg-primary hover:bg-primary rounded px-3 py-2 text-white hover:cursor-default hover:text-white`}
				>
					1
				</PaginationLink>
			</PaginationItem>
		);
	}

	render.push(
		<PaginationItem>
			<PaginationNext
				key="next"
				disabled={active === pagination || pagination === 0}
				onClick={() => onClick(active)}
				className="rounded px-3 py-2 disabled:opacity-50"
			/>
		</PaginationItem>
	);

	return (
		<div className="flex space-x-2">
			{render.map((C, i) => (
				<React.Fragment key={i}>{C}</React.Fragment>
			))}
		</div>
	);
};
PaginationItems.displayName = 'PaginationItems';

const PaginationShowAllLinkItems = ({
	total = 1,
	activeIndex,
	onClick
}: {
	total: number,
	activeIndex: number,
	onClick: (page: number) => void
}) => {
	const render = [];

	render.push(
		<PaginationItem>
			<PaginationPrevious
				key="prev"
				disabled={activeIndex === 0}
				onClick={() => onClick(activeIndex - 1)}
				className="rounded px-3 py-2 disabled:opacity-50"
			/>
		</PaginationItem>
	);

	if (total <= 10) {
		for (let i = 1; i <= total; i++) {
			render.push(
				<PaginationItem>
					<PaginationLink
						key={i}
						onClick={() => onClick(i - 1)}
						className={cn(`rounded px-3 py-2`, {
							'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
								activeIndex + 1 === i,
							'text-secondary bg-white': activeIndex + 1 !== i
						})}
					>
						{i}
					</PaginationLink>
				</PaginationItem>
			);
		}
	} else {
		render.push(
			<PaginationItem>
				<PaginationLink
					key={'first'}
					onClick={() => onClick(0)}
					className={cn(`rounded px-3 py-2`, {
						'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
							activeIndex === 0,
						'text-secondary bg-white': activeIndex !== 0
					})}
				>
					1
				</PaginationLink>
			</PaginationItem>
		);

		if (activeIndex > 3) {
			render.push(
				<PaginationItem>
					<PaginationEllipsis />
				</PaginationItem>
			);
		}

		const start = Math.max(2, activeIndex - 1);
		const end = Math.min(total - 1, activeIndex + 3);

		for (let i = start; i <= end; i++) {
			render.push(
				<PaginationItem>
					<PaginationLink
						key={i}
						onClick={() => onClick(i - 1)}
						className={cn(`rounded px-3 py-2`, {
							'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
								activeIndex + 1 === i,
							'text-secondary bg-white': activeIndex + 1 !== i
						})}
					>
						{i}
					</PaginationLink>
				</PaginationItem>
			);
		}

		if (activeIndex + 1 < total - 3) {
			render.push(
				<PaginationItem>
					<PaginationEllipsis />
				</PaginationItem>
			);
		}

		render.push(
			<PaginationItem>
				<PaginationLink
					key={total}
					onClick={() => onClick(total - 1)}
					className={cn(`rounded px-3 py-2`, {
						'bg-primary hover:bg-primary text-white hover:cursor-default hover:text-white':
							activeIndex + 1 === total,
						'text-secondary bg-white': activeIndex + 1 !== total
					})}
				>
					{total}
				</PaginationLink>
			</PaginationItem>
		);
	}

	render.push(
		<PaginationItem>
			<PaginationNext
				key="next"
				disabled={activeIndex + 1 === total}
				onClick={() => onClick(activeIndex + 1)}
				className="rounded px-3 py-2 disabled:opacity-50"
			/>
		</PaginationItem>
	);

	return (
		<div className="flex space-x-2">
			{render.map((C, i) => (
				<React.Fragment key={i}>{C}</React.Fragment>
			))}
		</div>
	);
};
PaginationShowAllLinkItems.displayName = 'PaginationShowAllLinkItems';

export {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationItems,
	PaginationShowAllLinkItems
};
